const BASE = '/api';

export class ApiError extends Error {
  code: string;
  retryable: boolean;
  actionUrl?: string;
  warnings?: Array<{ source: string; code: string; message: string }>;
  httpStatus: number;
  detail?: Record<string, unknown>;

  constructor(detail: string | Record<string, unknown>, status: number) {
    super(typeof detail === 'string' ? detail : (detail.message as string) || `HTTP ${status}`);
    this.name = 'ApiError';
    this.code = typeof detail === 'object' ? (detail.code as string) || (detail.error_code as string) || 'unknown' : 'unknown';
    this.retryable = typeof detail === 'object' ? (detail.retryable as boolean) ?? true : true;
    this.actionUrl = typeof detail === 'object' ? (detail.action_url as string) : undefined;
    this.warnings = typeof detail === 'object' ? (detail.warnings as any[]) : undefined;
    this.httpStatus = status;
    this.detail = typeof detail === 'object' ? detail : undefined;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  if (res.status === 401 && headers['Authorization']) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    const returnPath = window.location.pathname + window.location.search;
    window.location.href = `/login?redirect=${encodeURIComponent(returnPath)}`;
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new ApiError(body.detail || body.message || res.statusText, res.status);
  }

  return res.json();
}

type RequestExtras = { signal?: AbortSignal };

export const api = {
  get: <T>(path: string, extras?: RequestExtras) =>
    request<T>(path, { signal: extras?.signal }),
  post: <T>(path: string, data?: unknown, extras?: RequestExtras) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(data), signal: extras?.signal }),
  patch: <T>(path: string, data?: unknown, extras?: RequestExtras) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(data), signal: extras?.signal }),
  put: <T>(path: string, data?: unknown, extras?: RequestExtras) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(data), signal: extras?.signal }),
  delete: <T>(path: string, extras?: RequestExtras) =>
    request<T>(path, { method: 'DELETE', signal: extras?.signal }),
};

export async function fetchWithAuth(url: string, init: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Skip Content-Type for FormData -- browser sets multipart boundary automatically
  if (!(init.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }
  const response = await fetch(url, { ...init, headers });
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
    throw new ApiError('Unauthorized', 401);
  }
  if (!response.ok) {
    let message = response.statusText;
    try {
      const data = await response.clone().json();
      message = data.detail || data.message || message;
    } catch {}
    throw new ApiError(message, response.status);
  }
  return response;
}
